import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '@/services/api/endpoints/auth';
import { setSessionExpiredHandler } from '@/services/api/client';
import { tokenStorage } from '@/services/api/tokenStorage';
import type { AuthenticatedUser } from '@/types/domain';

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  /** True until the initial session restore has finished. */
  initialising: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Whether the signed-in user holds a permission. UI convenience only. */
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  const clearSession = useCallback(() => {
    tokenStorage.clear();
    setUser(null);
  }, []);

  /*
   * On load, a stored token pair is not proof of a valid session — it may have
   * been revoked, or the account deactivated, since the tab was last open. The
   * profile is fetched to find out, which also refreshes the permission set so
   * the UI reflects any change made while the user was away.
   */
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!tokenStorage.hasSession()) {
        setInitialising(false);
        return;
      }
      try {
        const profile = await authApi.me();
        if (!cancelled) {
          setUser(profile);
        }
      } catch {
        // Expired or revoked. The axios interceptor has already attempted a
        // refresh, so reaching here means the session is genuinely over.
        if (!cancelled) {
          clearSession();
        }
      } finally {
        if (!cancelled) {
          setInitialising(false);
        }
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  // The API client cannot import React state, so it calls back here when a
  // refresh fails and the session is definitively over.
  useEffect(() => {
    setSessionExpiredHandler(() => setUser(null));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await authApi.login({ email, password });
    tokenStorage.save({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
    setUser(tokens.user);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // Revoking server-side is best effort. Failing to reach the server must
      // still sign the user out locally, which is what they asked for.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshProfile = useCallback(async () => {
    setUser(await authApi.me());
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      initialising,
      isAuthenticated: user !== null,
      login,
      logout,
      refreshProfile,
      can: (permission: string) => permissions.has(permission),
      canAny: (required: string[]) =>
        required.length === 0 || required.some((permission) => permissions.has(permission)),
    };
  }, [user, initialising, login, logout, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
